/** angular */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
/** end angular */

/** services */
import { BydRhsAbstractService } from '../../../shared/services/byd-rhs-abstract.service';
/** end services */

/** models */
import { DsPinsExportResponse } from '../models/ds-pins-export-response.class';
/** end models */

/** interface */
import { DsPinsFiltersInterface } from '../interfaces/ds-pins-filters.interface';
import { DsPinsExportResponseInterface } from '../interfaces/ds-pins-export-response.interface';
/** end interface */

/** functions */
import { BydGenerateParams } from '../../../functions/byd-generate-params.function';

/** end functions */
@Injectable({
  providedIn: 'root',
})
export class DsPinsExportService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * The new export endpoint feature flag
   */
  private hasFlag = this.bydFeatureFlagService?.hasFlags('new_export_paths');

  /**
   * The endpoint base
   */
  private basePath = `${this.hasFlag ? '/dspin' : ''}/v1/projects/`;

  /**
   * Starts global pins export.
   *
   * @param projectId Selected project id
   * @param dsPinsFilters Pins filters to take into account
   * @returns Observable<DsPinsExportResponse>
   */
  public startGlobalExport(projectId: number, dsPinsFilters: DsPinsFiltersInterface): Observable<DsPinsExportResponse> {
    this.path = `${this.basePath}${projectId}/${this.hasFlag ? 'exports/excelGlobal' : 'pins/mailexport'}`;
    const params = BydGenerateParams<DsPinsFiltersInterface>(dsPinsFilters);
    return this.getModel<DsPinsExportResponse>({ params });
  }

  /**
   * Starts global pins export by companies.
   *
   * @param projectId Selected project id
   * @param dsPinsFilters Pins filters to take into account
   * @returns Observable<DsPinsExportResponse>
   */
  public startGlobalExportByCompanies(
    projectId: number,
    dsPinsFilters: DsPinsFiltersInterface
  ): Observable<DsPinsExportResponse> {
    this.path = `${this.basePath}${projectId}/${this.hasFlag ? 'exports/excelCompany' : 'pins/mailexportbycompanies'}`;
    const params = BydGenerateParams<DsPinsFiltersInterface>(dsPinsFilters);
    return this.getModel<DsPinsExportResponse>({ params });
  }

  /**
   * Starts Plan list photo export.
   *
   * @param projectId Selected project id
   * @param dsPinsFilters Pins filters to take into account
   * @returns Observable<DsPinsExportResponse>
   */
  public startPlanListPhotoExport(
    projectId: number,
    dsPinsFilters: DsPinsFiltersInterface
  ): Observable<DsPinsExportResponse> {
    this.path = `${this.basePath}${projectId}/${
      this.hasFlag ? 'exports/planListPhoto' : 'documents/pinsplanlistphotoexport?global=true'
    }`;
    const params = BydGenerateParams<DsPinsFiltersInterface>(dsPinsFilters);
    return this.getModel<DsPinsExportResponse>({ params });
  }

  /**
   * Starts Plan list photo export.
   *
   * @param projectId Selected project id
   * @param dsPinsFilters Pins filters to take into account
   * @returns Observable<DsPinsExportResponse>
   */
  public startPlanListPhotoExportByCompanies(
    projectId: number,
    dsPinsFilters: DsPinsFiltersInterface
  ): Observable<DsPinsExportResponse> {
    this.path = `${this.basePath}${projectId}/${
      this.hasFlag ? 'exports/companyPlanListPhoto' : 'documents/pinsplanlistphotoexport?global=false'
    }`;
    const params = BydGenerateParams<DsPinsFiltersInterface>(dsPinsFilters);
    return this.getModel<DsPinsExportResponse>({ params });
  }

  /**
   * Starts global pins export.
   *
   * @param projectId Selected project id
   * @param dsPinsFilters Pins filters to take into account
   * @returns Observable<DsPinsExportResponse>
   */
  public startStatisticsExport(
    projectId: number,
    dsPinsFilters: DsPinsFiltersInterface
  ): Observable<DsPinsExportResponse> {
    this.path = `${this.basePath}${projectId}/${this.hasFlag ? 'exports/statistic' : 'pins/statisticsmailexport'}`;
    const params = BydGenerateParams<DsPinsFiltersInterface>(dsPinsFilters);
    return this.getModel<DsPinsExportResponse>({ params });
  }

  /**
   * Starts BIM Document export.
   *
   * @param projectId Selected project id
   * @param dsPinsFilters Pins filters to take into account
   * @returns Observable<DsPinsExportResponse>
   */
  public startBimDocumentExport(
    projectId: number,
    dsPinsFilters: DsPinsFiltersInterface
  ): Observable<DsPinsExportResponse> {
    this.path = `${this.basePath}${projectId}/${this.hasFlag ? 'exports/bimDocument' : 'documents/bimdocumentexport'}`;
    const params = BydGenerateParams<DsPinsFiltersInterface>(dsPinsFilters);
    return this.getModel<DsPinsExportResponse>({ params });
  }

  /**
   * Start custom word export.
   *
   * @param projectId Project ID.
   * @param templateId Template ID.
   * @param dsPinsFilters Pins Filter.
   * @returns Observable<DsPinsExportResponse>.
   */
  public startCustomWordExport(
    projectId: number,
    templateId: number,
    dsPinsFilters: DsPinsFiltersInterface
  ): Observable<DsPinsExportResponse> {
    this.path = `/dspin/v1/projects/${projectId}/word/${templateId}/exports/planListPhoto`;
    const params = BydGenerateParams<DsPinsFiltersInterface>(dsPinsFilters);
    return this.getModel<DsPinsExportResponse>({ params });
  }

  /**
   * Start Pins Photo Export.
   *
   * @param projectId Project ID.
   * @param dsPinsFilters Filters.
   * @returns Observable<Blob>.
   */
  public startPinsPhotoExport(
    projectId: number,
    dsPinsFilters: DsPinsFiltersInterface
  ): Observable<DsPinsExportResponse> {
    this.path = `/v1/dspin/projects/${projectId}/exports/photos`;
    const params = BydGenerateParams<DsPinsFiltersInterface>(dsPinsFilters);
    return this.getModel<DsPinsExportResponse>({ params });
  }

  /**
   * Create DsPinsExportResponse instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `DsPinsExportResponse` instance.
   */
  public createNewInstance(jsonObject: DsPinsExportResponseInterface) {
    return new DsPinsExportResponse(jsonObject);
  }
}
